import { router } from "@inertiajs/react";
import { Anchor, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { DataTableColumn } from "mantine-datatable";
import { getFullName } from "../../helpers/stringHelper";
import { useYearContext } from "../../hooks/useYearContext";
import { reportTypeTranslations } from "../../models/enums/reportType";
import { statusTranslations } from "../../models/enums/status";
import type { Report } from "../../models/reports/report";
import { apprentice_path, group_path } from "../../routes";
import { DataTable, type DataTableProps } from "../datatable/DataTable";
import { FileSyncIndicator } from "../indicator/FileSyncIndicator";

type ReportTableProps = {
  reports: Report[];
} & Required<
  Pick<
    DataTableProps<Report>,
    | "onEdit"
    | "onDelete"
    | "onSortStatusChange"
    | "sortStatus"
    | "renderAdditionalActions"
    | "onSelectedRecordsChange"
    | "selectedRecords"
    | "pagination"
    | "onPageChange"
    | "fetching"
  >
>;

export function ReportTable({ reports, ...props }: ReportTableProps) {
  const { pathWithYearContext } = useYearContext();

  const columns: DataTableColumn<Report>[] = [
    {
      accessor: "updated",
      title: "",
      width: "0%",
      render: (report) => {
        const report_date = Date.parse(report.updated_at);
        const config_date = Date.parse(report.report_configuration.updated_at);
        const warn = config_date > report_date;
        const warnText = (
          <>
            <Text>Der Bericht ist nicht mehr synchron mit der Berichtsvorlage.</Text>
            <Text>Bitte prüfen Sie die Änderungen beim Bearbeiten des Berichts und speichern Sie diesen erneut.</Text>
          </>
        );
        const okText = <Text>Der Bericht ist synchron mit der Vorlage.</Text>;
        return <FileSyncIndicator warn={warn} warnText={warnText} okText={okText} warnIcon={<IconAlertTriangle />} />;
      },
    },
    {
      accessor: "report_type",
      title: "Bericht",
      sortable: true,
      render: (report) => reportTypeTranslations[report.report_configuration.report_type],
    },
    {
      accessor: "apprentice",
      title: "Name",
      sortable: true,
      render: (report) => (
        <Anchor onClick={() => router.get(pathWithYearContext(apprentice_path, report.apprentice.id))}>
          {getFullName(report.apprentice)}
        </Anchor>
      ),
    },
    {
      accessor: "group.name",
      title: "Gruppe",
      sortable: true,
      render: (report) => (
        <Anchor onClick={() => router.get(pathWithYearContext(group_path, report.apprentice.group.id))}>
          {report.apprentice.group.name}
        </Anchor>
      ),
    },
    {
      accessor: "coach",
      title: "Verantwortlicher Coach",
      sortable: true,
      render: (report) => getFullName(report.coach),
    },
    {
      accessor: "status",
      title: "Status",
      sortable: true,
      render: (report) => statusTranslations[report.status],
    },
  ];

  return <DataTable records={reports} columns={columns} {...props} />;
}
